import * as lockr from 'lockr'
import type { UserDataType } from '../types/UserDataType'
import {
  combineUserDataChanges,
  createEmptyUserDataChanges,
  createUserDataChangesFromPatch,
  hasUserDataChanges,
  removeUploadedUserDataChanges,
  type UserDataChanges,
} from './userDataChanges'
import { getDataTimestamp } from './userDataMerge'

const DATA_KEY = 'userData'
const PENDING_CHANGES_KEY = 'userDataPendingChanges'

// Keys of the previous sync design, migrated once and then removed.
const LEGACY_PENDING_PATCH_KEY = 'userDataSyncPendingPatch'
const LEGACY_DIRTY_KEY = 'userDataSyncDirty'
const LEGACY_SYNC_KEYS = [
  LEGACY_DIRTY_KEY,
  'userDataCloudVersion',
  'userDataCloudPositions',
  LEGACY_PENDING_PATCH_KEY,
]

type AuthIdentity = {
  uid: string
  email?: string | null
  displayName?: string | null
}

/** Remove fields that are not part of the current user-data schema. */
const sanitizeUserDataRecord = (
  data: Partial<UserDataType>,
  initialState: UserDataType,
) => {
  const filteredData = JSON.parse(JSON.stringify(data))
  const keys = Object.keys(filteredData) as Array<keyof UserDataType>
  keys.forEach((key) => {
    if (!(key in initialState)) delete filteredData[key]
  })
  return filteredData as UserDataType
}

/**
 * Build a safe user-data record from persisted/cloud data and the current auth identity.
 * Defaults are added, unknown fields are removed, and identity fields are refreshed.
 */
export const normalizeUserDataRecord = (
  data: Partial<UserDataType> | null,
  initialState: UserDataType,
  identity: AuthIdentity | null,
) =>
  sanitizeUserDataRecord(
    {
      ...initialState,
      ...(data || {}),
      ...(identity
        ? {
            uid: identity.uid,
            email: identity.email || null,
            displayName: identity.displayName || null,
          }
        : {}),
    },
    initialState,
  )

/**
 * Sanitize a user-data record and assign a fresh timestamp for a real local mutation.
 */
export const withUserDataTimestamp = (
  data: Partial<UserDataType>,
  initialState: UserDataType,
) =>
  sanitizeUserDataRecord({ ...data, updatedAt: new Date().toISOString() }, initialState)

/** Read the complete user-data record saved in browser storage, if one exists. */
export const readLocalUserData = () => lockr.get(DATA_KEY) as Partial<UserDataType> | null

/** Persist the complete local user-data record used for offline startup. */
export const writeLocalUserData = (data: UserDataType) => {
  lockr.set(DATA_KEY, data)
}

/** Read the local edits waiting to be uploaded. */
export const readPendingUserDataChanges = (): UserDataChanges =>
  (lockr.get(PENDING_CHANGES_KEY) as UserDataChanges | undefined) ||
  createEmptyUserDataChanges()

/** Persist the pending edits, removing the key when nothing is pending. */
const writePendingUserDataChanges = (changes: UserDataChanges) => {
  if (hasUserDataChanges(changes)) {
    lockr.set(PENDING_CHANGES_KEY, changes)
  } else {
    lockr.rm(PENDING_CHANGES_KEY)
  }
}

/** Add a new local edit to the pending edits. */
export const addPendingUserDataChanges = (changes: UserDataChanges) => {
  if (!hasUserDataChanges(changes)) return
  writePendingUserDataChanges(
    combineUserDataChanges(readPendingUserDataChanges(), changes),
  )
}

/**
 * Add older edits underneath the pending edits: a field that already has a pending
 * edit keeps it, and only the other fields are added.
 */
const addOlderPendingUserDataChanges = (olderChanges: UserDataChanges) => {
  if (!hasUserDataChanges(olderChanges)) return
  writePendingUserDataChanges(
    combineUserDataChanges(olderChanges, readPendingUserDataChanges()),
  )
}

/** Remove uploaded edits; edits made again during the upload stay pending. */
export const clearUploadedUserDataChanges = (uploadedChanges: UserDataChanges) => {
  writePendingUserDataChanges(
    removeUploadedUserDataChanges(readPendingUserDataChanges(), uploadedChanges),
  )
}

/**
 * Convert unsynced edits left by the previous sync design into pending changes,
 * then remove the old sync keys. Converted fields get the local record's last edit
 * time, which is the newest time any of them could have changed.
 *
 * - A pending patch lists the changed fields, so only those are converted.
 * - Older clients only set a dirty flag. As before, the whole local record is kept
 *   only when it is newer than the cloud record; otherwise the cloud wins. This
 *   needs the cloud record, so with `cloudData === undefined` (cloud not read yet)
 *   the old state is left in place for a later load.
 *
 * The converted edits go underneath existing pending edits. While sign-in is still
 * loading offline, the user can edit before this runs; those edits are newer than
 * anything left by the old client, so they must not be replaced. (Their edit also
 * moves the local record's `updatedAt`, so the old edits would otherwise look newest.)
 *
 * Returns whether the migration is finished; once it is, it never needs to run again.
 */
export const migrateLegacyUserDataSyncState = (
  cloudData: Partial<UserDataType> | null | undefined,
): boolean => {
  const legacyPatch = lockr.get(LEGACY_PENDING_PATCH_KEY) as
    Partial<UserDataType> | undefined
  const hasLegacyPatch = Boolean(legacyPatch && Object.keys(legacyPatch).length > 0)
  const isLegacyDirty = lockr.get(LEGACY_DIRTY_KEY) === true
  const localData = readLocalUserData()
  const editTime = getDataTimestamp(localData) || Date.now()

  if (hasLegacyPatch) {
    addOlderPendingUserDataChanges(createUserDataChangesFromPatch(legacyPatch!, editTime))
  } else if (isLegacyDirty && localData) {
    if (cloudData === undefined) return false
    if (editTime > getDataTimestamp(cloudData)) {
      addOlderPendingUserDataChanges(createUserDataChangesFromPatch(localData, editTime))
    }
  }

  LEGACY_SYNC_KEYS.forEach((key) => lockr.rm(key))
  return true
}
